import { fetchAuthSession } from 'aws-amplify/auth';

/**
 * API client for the Contracting dashboard.
 *
 * Talks to the same Express server as the finance dashboard, but only ever
 * under /api/contracting. In dev the Vite proxy forwards to localhost:4000; in
 * production set VITE_API_URL to the absolute API origin.
 */
const BASE_URL = import.meta.env.VITE_API_URL ?? '/api';

export class ApiError extends Error {
  status: number;
  data: unknown;

  constructor(status: number, data: unknown) {
    super(extractMessage(status, data));
    this.status = status;
    this.data = data;
  }
}

/** The server wraps failures as { error: { message } }; plain { message } too. */
function extractMessage(status: number, data: unknown): string {
  if (data && typeof data === 'object') {
    const body = data as { message?: unknown; error?: { message?: unknown } };
    if (typeof body.message === 'string') return body.message;
    if (typeof body.error?.message === 'string') return body.error.message;
  }
  return `API error ${status}`;
}

async function authHeaders(): Promise<Record<string, string>> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.accessToken?.toString();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

function buildUrl(path: string, params?: Record<string, string>): string {
  const url = new URL(`${BASE_URL}${path}`, window.location.origin);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') url.searchParams.set(key, value);
    }
  }
  return url.toString();
}

async function parse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new ApiError(res.status, data);
  }
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export async function apiGet<T = unknown>(
  path: string,
  params?: Record<string, string>,
): Promise<T> {
  const res = await fetch(buildUrl(path, params), {
    headers: await authHeaders(),
  });
  return parse<T>(res);
}

async function withBody<T>(
  method: 'POST' | 'PUT' | 'PATCH',
  path: string,
  body: unknown,
): Promise<T> {
  const res = await fetch(buildUrl(path), {
    method,
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    body: JSON.stringify(body),
  });
  return parse<T>(res);
}

export const apiPost = <T = unknown>(path: string, body: unknown) =>
  withBody<T>('POST', path, body);

export const apiPut = <T = unknown>(path: string, body: unknown) =>
  withBody<T>('PUT', path, body);

export const apiPatch = <T = unknown>(path: string, body: unknown) =>
  withBody<T>('PATCH', path, body);

export async function apiDelete<T = unknown>(path: string): Promise<T> {
  const res = await fetch(buildUrl(path), {
    method: 'DELETE',
    headers: await authHeaders(),
  });
  return parse<T>(res);
}

/** Multipart upload — the browser sets its own Content-Type boundary. */
export async function apiPostFormData<T = unknown>(
  path: string,
  formData: FormData,
): Promise<T> {
  const res = await fetch(buildUrl(path), {
    method: 'POST',
    headers: await authHeaders(),
    body: formData,
  });
  return parse<T>(res);
}

/** Streams a file response to disk, honouring the server's filename. */
export async function apiDownloadFile(
  path: string,
  params?: Record<string, string>,
  defaultFilename = 'download',
): Promise<void> {
  const res = await fetch(buildUrl(path, params), {
    headers: await authHeaders(),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new ApiError(res.status, data);
  }

  const disposition = res.headers.get('Content-Disposition');
  const match = disposition?.match(/filename="?([^"]+)"?/);
  const filename = match?.[1] ?? defaultFilename;

  const objectUrl = URL.createObjectURL(await res.blob());
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(objectUrl);
}
